
import React from 'react';
import { UserProfile, Client } from '@/shared/types';

interface TemplateProps {
  profile: UserProfile;
  client?: Client | null;
  title: string;
  metaData: { label: string; value: string }[];
  children: React.ReactNode;
  footerNote?: string;
  type: string;
  containerClass: string;
  LogoImage: React.FC<{ size: 'sm' | 'md' | 'lg' | 'xl'; className?: string; light?: boolean }>;
  hideHeader?: boolean;
}

const BoldTemplate: React.FC<TemplateProps> = ({
  profile, client, title, metaData, children, footerNote, type, containerClass, LogoImage, hideHeader = false
}) => {
  return (
    <div className={containerClass}>
       <div className="flex h-full absolute inset-0">
          {!hideHeader ? (
            <>
              {/* Left Sidebar */}
              <div style={{ backgroundColor: profile.brandColor }} className="w-[32%] h-full text-white p-10 flex flex-col print:w-[32%] print:bg-neutral-800">
                 <div className="mb-12">
                    <div className="bg-white/10 p-6 rounded-2xl mb-8 backdrop-blur-md inline-block shadow-lg">
                       <LogoImage size="md" light={true} className="mix-blend-normal" />
                    </div>

                    <div className="space-y-6">
                       <div>
                          <h2 className="text-xs font-bold opacity-50 uppercase tracking-widest mb-2">From</h2>
                          <h1 className="font-bold text-2xl leading-tight">{profile.name}</h1>
                       </div>
                       <div className="text-sm opacity-80 space-y-1.5 font-light leading-relaxed">
                          <p>{profile.address}</p>
                          <p>{profile.email}</p>
                          <p>{profile.phone}</p>
                       </div>
                    </div>
                 </div>

                 <div className="space-y-8 flex-1">
                     <div>
                        <h2 className="text-xs font-bold opacity-50 uppercase tracking-widest mb-3 border-t border-white/20 pt-8">To</h2>
                        {client ? (
                          <div className="space-y-1.5">
                            <p className="font-bold text-xl">{client.name}</p>
                            <p className="opacity-90 text-sm font-medium">{client.company}</p>
                            <p className="opacity-70 text-sm font-light">{client.address}</p>
                            <p className="opacity-70 text-sm font-light">{client.email}</p>
                          </div>
                        ) : <p className="opacity-50 italic">No Client</p>}
                     </div>
                 </div>

                 <div className="pt-8 border-t border-white/20 mt-auto">
                    <p className="text-xs opacity-50 font-medium tracking-wider">POWERED BY SWAYSUITE</p>
                 </div>
              </div>

              {/* Right Content */}
              <div className="w-[68%] h-full p-12 flex flex-col">
                 <div className="text-right border-b-2 border-neutral-100 pb-10 mb-10">
                    <h1 className="text-6xl font-black text-neutral-900 uppercase tracking-tighter mb-2 opacity-10">{type}</h1>
                    <div className="-mt-8">
                       <p className="text-2xl font-bold" style={{ color: profile.brandColor }}>{title}</p>
                       <div className="flex justify-end gap-8 mt-6">
                          {metaData.map((m, i) => (
                            <div key={i} className="text-right">
                              <p className="text-xs font-bold text-neutral-400 uppercase tracking-wider mb-1">{m.label}</p>
                              <p className="font-bold text-neutral-900 text-lg">{m.value}</p>
                            </div>
                          ))}
                       </div>
                    </div>
                 </div>

                 <div className="flex-1">
                    {children}
                 </div>

                 <div className="mt-auto pt-8 border-t-4" style={{ borderColor: profile.brandColor }}>
                    <p className="font-bold text-neutral-900 mb-1 text-sm uppercase tracking-wide">Notes & Terms</p>
                    <p className="text-neutral-500 text-sm leading-relaxed">{footerNote || "Payment is due within 14 days. Thank you for your business."}</p>
                 </div>
              </div>
            </>
          ) : (
            <div className="w-full h-full p-12 flex flex-col">
               <div className="flex-1">
                  {children}
               </div>

               <div className="mt-auto pt-8 border-t-4" style={{ borderColor: profile.brandColor }}>
                  <p className="font-bold text-neutral-900 mb-1 text-sm uppercase tracking-wide">Notes & Terms</p>
                  <p className="text-neutral-500 text-sm leading-relaxed">{footerNote || "Payment is due within 14 days. Thank you for your business."}</p>
               </div>
            </div>
          )}
       </div>
    </div>
  );
};

export default BoldTemplate;
